import { MAX_AGENT_INSTRUCTION_LENGTH } from '@/_data/agentChat';
import type { AgentThreadView, AgentChatCommand, AgentChatConnection, AgentChatSession } from '@/_contract/agentChat';

export interface AgentChatBackend {
    read(threadId: string, signal: AbortSignal): Promise<AgentThreadView>;
    send(instruction: string, threadId: string | undefined, signal: AbortSignal): Promise<AgentThreadView>;
    initialize?(signal: AbortSignal): Promise<AgentChatConnection>;
    command?(threadId: string, command: AgentChatCommand, signal: AbortSignal): Promise<AgentThreadView>;
    watch?(threadId: string, onView: (view: AgentThreadView) => void, onError: (message: string) => void): () => void;
}

export class AgentChatApiError extends Error {
    constructor(message: string, readonly status: number) { super(message); }
}

export type AgentChatState = {
    view: AgentThreadView | null;
    threadId?: string;
    draft: string;
    sending: boolean;
    loading: boolean;
    error?: string;
    transport?: 'legacy' | 'ahp';
    sessions?: AgentChatSession[];
    reconnecting?: boolean;
    inputValues?: Record<string, Record<string, string>>;
    voiceBusy?: boolean;
    development?: AgentChatConnection['development'];
    changesetId?: 'session' | 'turn' | 'uncommitted' | 'planned' | 'none';
};

export function chatWorking(state: AgentChatState) {
    return !!state.view && !['idle', 'error'].includes(state.view.status);
}

export function chatLocked(state: AgentChatState) {
    return !!chatInputBlockReason(state);
}

export function chatInputBlockReason(state: AgentChatState): string | undefined {
    if (state.voiceBusy) return 'Voice input is already in progress. Finish or cancel it first.';
    if (state.sending) return 'Your previous message is still being sent. Wait for acknowledgement.';
    if (state.reconnecting) return 'Reconnecting to Forge. Wait for the conversation to reconnect.';
    if (state.loading) return 'Loading the conversation. Wait for it to finish.';
    if (state.view?.plans?.some(plan => plan.pending)) return 'A plan is waiting for review. Finish planning in the Agent panel first.';
    if (state.view?.approvals?.length || state.view?.inputs?.length || state.view?.status === 'input') {
        return 'The Agent needs your answer. Review the pending request in the Agent panel.';
    }
    if (chatWorking(state)) return 'The Agent is still working. Wait for the reply or cancel the turn in the Agent panel.';
    if (state.threadId && !state.view) return 'The conversation is unavailable. Retry the connection in the Agent panel.';
    return undefined;
}

export function createAgentChatController(backend: AgentChatBackend, storage: () => Storage, storageKey: string, pollMs = 3000) {
    let state: AgentChatState = { view: null, draft: '', sending: false, loading: true };
    const listeners = new Set<() => void>();
    let lifetime: AbortController | undefined;
    let reading: AbortSignal | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopStream: (() => void) | undefined;
    let streamVersion = 0;
    let selection: AbortController | undefined;
    let key = storageKey;
    const update = (patch: Partial<AgentChatState>) => {
        state = { ...state, ...patch };
        listeners.forEach(listener => listener());
    };
    const store = (id?: string) => {
        try {
            if (id) storage().setItem(key, id);
            else storage().removeItem(key);
        } catch (error) {
            console.warn('[agent chat] Could not persist chat:', error);
            update({ error: 'Browser storage unavailable. This chat may not survive a reload.' });
        }
    };
    const schedule = (signal: AbortSignal) => {
        if (signal.aborted || state.transport === 'ahp') return;
        clearTimeout(timer);
        if (chatWorking(state)) timer = setTimeout(() => { void refresh(signal); }, pollMs);
    };
    const stream = (signal: AbortSignal) => {
        const version = ++streamVersion;
        stopStream?.();
        stopStream = undefined;
        if (signal.aborted || state.transport !== 'ahp' || !state.threadId || !backend.watch) return;
        stopStream = backend.watch(state.threadId, view => {
            if (!signal.aborted && version === streamVersion) update({ view, loading: false, error: undefined, reconnecting: false });
        }, message => {
            if (!signal.aborted && version === streamVersion) update({ error: message, loading: false, reconnecting: true });
        });
    };
    const refresh = async (signal: AbortSignal) => {
        if (!state.threadId || reading === signal) return;
        reading = signal;
        try {
            const view = await backend.read(state.threadId, signal);
            if (!signal.aborted) update({ view, loading: false, error: undefined, reconnecting: false });
        } catch (error) {
            if (signal.aborted) return;
            console.error('[agent chat] Refresh failed:', error);
            if (error instanceof AgentChatApiError && error.status === 404) {
                update({ threadId: undefined, view: null, loading: false, reconnecting: false, error: 'The saved chat is no longer available. Start a new request.' });
                store();
            } else update({ loading: false, reconnecting: state.transport === 'ahp', error: error instanceof Error ? error.message : String(error) });
        } finally { if (reading === signal) reading = undefined; }
        if (!signal.aborted) {
            schedule(signal);
            if (state.transport === 'ahp') stream(signal);
        }
    };
    const beginSelection = () => {
        streamVersion++;
        selection?.abort();
        stopStream?.();
        stopStream = undefined;
        clearTimeout(timer);
        selection = new AbortController();
        return selection.signal;
    };
    const restore = async (signal: AbortSignal) => {
        try {
            if (backend.initialize) {
                const connection = await backend.initialize(signal);
                if (signal.aborted) return;
                update(connection);
                key = connection.transport === 'ahp'
                    ? `${storageKey}:ahp${connection.development ? `:${connection.development.id}` : ''}` : storageKey;
            }
            let id: string | null;
            try { id = storage().getItem(key); }
            catch (error) {
                console.warn('[agent chat] Could not restore chat:', error);
                update({ loading: false, error: 'Browser storage unavailable. Chat restoration is disabled.' });
                return;
            }
            if (signal.aborted) return;
            update({ threadId: id || undefined, ...(!id ? { view: null } : {}), loading: !!id, sending: false, reconnecting: false });
        } catch (error) {
            if (signal.aborted) return;
            console.error('[agent chat] Could not initialize chat:', error);
            update({ loading: false, error: error instanceof Error ? error.message : String(error) });
            return;
        }
        if (state.threadId) void refresh(beginSelection());
    };
    return {
        getSnapshot: () => state,
        subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; },
        start() {
            const active = new AbortController();
            lifetime = active;
            const signal = active.signal;
            beginSelection();
            update({ loading: true });
            void restore(signal);
            return () => { active.abort(); selection?.abort(); stopStream?.(); clearTimeout(timer); };
        },
        setDraft(draft: string) {
            if (!chatLocked(state)) update({ draft: draft.slice(0, MAX_AGENT_INSTRUCTION_LENGTH) });
        },
        selectChangeset(id: NonNullable<AgentChatState['changesetId']>) {
            if (id !== 'none' && id !== 'planned' && !state.view?.changesets?.some(changes => changes.id === id)) {
                update({ error: 'This changeset is no longer available. Reconnect to refresh the host catalogue.' });
                return;
            }
            update({ changesetId: id });
        },
        beginVoiceInput() {
            if (chatLocked(state)) return false;
            update({ voiceBusy: true });
            return true;
        },
        finishVoiceInput(text?: string) {
            if (text !== undefined) {
                if (!state.voiceBusy) throw new Error('Voice draft reservation has ended.');
                const draft = [state.draft, text.trim()].filter(Boolean).join('\n');
                if (draft.length > MAX_AGENT_INSTRUCTION_LENGTH) throw new Error('Transcript plus draft exceeds 4000 characters. Cancel voice input and shorten the draft before recording again.');
                update({ draft, voiceBusy: false });
            } else update({ voiceBusy: false });
        },
        async send() {
            const instruction = state.draft.trim();
            const signal = selection?.signal ?? lifetime?.signal;
            if (!signal || signal.aborted || chatLocked(state) || !instruction) return;
            update({ sending: true, error: undefined });
            try {
                const view = await backend.send(instruction, state.threadId, signal);
                if (signal.aborted) return;
                update({ view: stopStream && state.threadId === view.threadId ? state.view : view, threadId: view.threadId, draft: '', sending: false });
                store(view.threadId);
                schedule(signal);
                stream(signal);
                if (state.transport === 'ahp' && !state.sessions?.some(session => session.threadId === view.threadId)) {
                    update({ sessions: [{ threadId: view.threadId, title: instruction.slice(0, 60) }, ...(state.sessions ?? [])] });
                }
            } catch (error) {
                if (signal.aborted) return;
                console.error('[agent chat] Send failed:', error);
                update({ sending: false, error: error instanceof Error ? error.message : String(error) });
            }
        },
        retry() {
            if (!lifetime || lifetime.signal.aborted || state.loading || state.sending) return;
            clearTimeout(timer);
            update({ loading: true, error: undefined });
            if (state.threadId) void refresh(beginSelection());
            else void restore(lifetime.signal);
        },
        newChat() {
            if (state.voiceBusy || state.loading || state.sending || chatWorking(state)) return;
            clearTimeout(timer);
            beginSelection();
            update({ view: null, threadId: undefined, error: undefined, reconnecting: false, inputValues: undefined });
            store();
        },
        selectSession(threadId: string) {
            if (state.voiceBusy || state.sending || state.loading || !state.sessions?.some(session => session.threadId === threadId)) return;
            const signal = beginSelection();
            update({ view: null, threadId, loading: true, error: undefined, reconnecting: false, inputValues: undefined });
            store(threadId);
            void refresh(signal);
        },
        setInputValue(requestId: string, questionId: string, value: string) {
            if (state.sending || state.loading) return;
            update({ inputValues: { ...state.inputValues,
                [requestId]: { ...state.inputValues?.[requestId], [questionId]: value.slice(0, MAX_AGENT_INSTRUCTION_LENGTH) } } });
        },
        async refreshSessions() {
            const signal = lifetime?.signal;
            if (!signal || signal.aborted || !backend.initialize) return;
            try {
                const connection = await backend.initialize(signal);
                if (!signal.aborted) update({ sessions: connection.sessions, error: undefined });
            } catch (error) {
                if (!signal.aborted) update({ error: error instanceof Error ? error.message : String(error) });
            }
        },
        async command(command: AgentChatCommand) {
            const signal = selection?.signal ?? lifetime?.signal;
            if (!signal || signal.aborted || state.sending || state.loading || state.reconnecting || !state.threadId || !backend.command) return;
            update({ sending: true, error: undefined });
            try {
                const view = await backend.command(state.threadId, command, signal);
                if (!signal.aborted) update({ ...(stopStream ? {} : { view }), sending: false });
            } catch (error) {
                if (signal.aborted) return;
                console.error('[agent chat] Command failed:', error);
                update({ sending: false, error: error instanceof Error ? error.message : String(error) });
            }
        },
    };
}
